import { IconX } from './Icons';
import { useState, useEffect } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  settings: any;
  moduleName: string;
  moduleType: string;
  onSave: (settings: any) => void;
}

export default function JsonSettingsEditor({ isOpen, onClose, settings, moduleName, moduleType, onSave }: Props) {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setJsonText(JSON.stringify(settings || {}, null, 2));
      setError(null);
      setCopied(false);
    }
  }, [isOpen, settings]);

  function handleSave() {
    try {
      const parsed = JSON.parse(jsonText);
      setError(null);
      onSave(parsed);
      onClose();
    } catch (e) {
      setError('JSON 格式錯誤: ' + (e as Error).message);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(jsonText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleFormat() {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e) {
      setError('JSON 格式錯誤: ' + (e as Error).message);
    }
  }

  function handleImport() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const text = await file.text();
          const parsed = JSON.parse(text);
          setJsonText(JSON.stringify(parsed, null, 2));
          setError(null);
        } catch (err) {
          setError('檔案讀取失敗: ' + (err as Error).message);
        }
      }
    };
    input.click();
  }

  function handleExport() {
    try {
      const parsed = JSON.parse(jsonText);
      const blob = new Blob([JSON.stringify(parsed, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${moduleType}_settings.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('匯出失敗: JSON 格式錯誤');
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0F1826]/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] flex flex-col pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">JSON 設定編輯器</h2>
            <p className="text-sm text-gray-500">{moduleName} ({moduleType})</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="w-8 h-8 flex items-center justify-center text-muted hover:text-ink hover:bg-ground rounded-md"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center gap-2">
          <button
            onClick={handleFormat}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            格式化
          </button>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {copied ? '已複製!' : '複製'}
          </button>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={handleImport}
            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg"
          >
            匯入 JSON
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg"
          >
            匯出 JSON
          </button>
        </div>

        {/* Editor */}
        <div className="flex-1 p-6 overflow-hidden">
          <textarea
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setError(null);
            }}
            className="w-full h-full min-h-[300px] px-4 py-3 font-mono text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary resize-none"
            placeholder="{}"
            spellCheck={false}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-2">
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary/90 rounded-lg"
          >
            儲存設定
          </button>
        </div>
      </div>
    </div>
  );
}
